// ─────────────────────────────────────────────────────────────────────────────
// Shared presentational components.
//
// Small, typed and dumb: they take props and render. No data fetching and no
// business rules live here, which is what keeps them safe to change.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, type ReactElement, type ReactNode } from "react";
import { inclusiveDays, isIsoDate } from "@shared/dates";
import { formatNumber } from "@shared/intl";
import { useI18n } from "../i18n";
import { apiError } from "../api";

// ─────────────────────────────────────────────────────────────────────────────
// How long is the range I have just typed?
//
// Every dialog that takes a start and an end had left the person to work that
// out in their head, and in the leave dialogs the number they were working out
// was the number of days about to be deducted from their balance. They could
// not see it until after the request was approved.
//
// It uses `inclusiveDays` from @shared/dates — the SAME function the server
// counts with, not a second implementation that agrees with it today. A browser
// that disagreed with the server about the size of a request would be worse
// than showing nothing, because it would be believed.
// ─────────────────────────────────────────────────────────────────────────────
export function DayCount({ from, to, note }: {
  readonly from: string;
  readonly to: string;
  /** Shown after a separator — the balance this count is about to be taken from. */
  readonly note?: ReactNode;
}): ReactElement | null {
  const { days } = useI18n();

  // No count while the range is incomplete — a half-typed range reads as a huge
  // or a negative one, and a number that flickers is worse than one that waits.
  // The note still shows, so the balance keeps its place on the form instead of
  // appearing and moving everything down once both dates are in.
  const ready = isIsoDate(from) && isIsoDate(to);
  const count = ready ? inclusiveDays(from, to) : 0; // 0 when the end precedes the start.

  if (count <= 0) {
    if (note === undefined || note === null) return null;
    return <p className="day-count muted">{note}</p>;
  }

  return (
    <p className="day-count">
      <strong>{days(count)}</strong>
      {note !== undefined && note !== null && <span className="muted"> · {note}</span>}
    </p>
  );
}

export function Card({ title, actions, className, children }: {
  title?: ReactNode;
  actions?: ReactNode;
  /** A variant, for the rare card whose contents want different chrome. */
  className?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <section className={className === undefined ? "card" : `card ${className}`}>
      {(title !== undefined || actions !== undefined) && (
        <header className="card-header">
          {title !== undefined ? <h2>{title}</h2> : <span />}
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

export function Stat({ value, label }: { value: ReactNode; label: string }): ReactElement {
  return (
    <div className="stat">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

type Tone = "neutral" | "success" | "warning" | "danger" | "info";

export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }): ReactElement {
  const cls = tone === "neutral" ? "badge" : `badge badge-${tone}`;
  return <span className={cls}>{children}</span>;
}

/**
 * Renders a server error using the message the SERVER chose, in the user's
 * language. The UI never invents its own wording for a business rule — that is
 * how the same rejection ends up explained three different ways.
 */
export function ErrorNotice({ error }: { error: unknown }): ReactElement | null {
  const { locale } = useI18n();
  if (error === null || error === undefined) return null;
  const parsed = apiError(error);
  const message = locale === "ar" && parsed.messageAr !== undefined ? parsed.messageAr : parsed.message;
  return (
    <div className="alert alert-error" role="alert">
      {message}
    </div>
  );
}

export function Loading(): ReactElement {
  const { t } = useI18n();
  return (
    <div className="empty" aria-live="polite">
      {t("common.loading")}
    </div>
  );
}

export function Empty({ message }: { message?: string }): ReactElement {
  const { t } = useI18n();
  return <div className="empty">{message ?? t("common.noResults")}</div>;
}

export function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }): ReactElement {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {error !== undefined && <span className="field-error">{error}</span>}
    </div>
  );
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }): ReactElement {
  const { t } = useI18n();
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="card-header">
          <h2>{title}</h2>
          <button type="button" className="btn btn-sm" onClick={onClose}>{t("common.close")}</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Confirmation before something irreversible.
 *
 * One component for every destructive action, so "are you sure" looks and behaves
 * the same everywhere and no screen invents its own.
 *
 * `typeToConfirm` raises the bar for actions with financial or legal weight —
 * terminating employment, voiding an issued document — by requiring the word to be
 * typed. A dialog that can be dismissed by muscle memory is not a safeguard.
 */
export function ConfirmDialog({
  title, message, confirmLabel, tone = "danger", typeToConfirm, busy = false, onConfirm, onCancel,
}: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  tone?: "danger" | "primary";
  /** When set, the confirm button stays disabled until this word is typed. */
  typeToConfirm?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): ReactElement {
  const { t } = useI18n();
  const [typed, setTyped] = useState("");
  const unlocked = typeToConfirm === undefined || typed.trim() === typeToConfirm;

  return (
    <Modal title={title} onClose={onCancel}>
      <div className="confirm-body">{message}</div>

      {typeToConfirm !== undefined && (
        <Field label={t("common.confirmTypeToProceed", { word: typeToConfirm })}>
          <input
            value={typed}
            autoComplete="off"
            onChange={(e) => setTyped(e.target.value)}
          />
        </Field>
      )}

      <div className="confirm-actions">
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          {t("common.cancel")}
        </button>
        <button
          type="button"
          className={tone === "danger" ? "btn btn-danger" : "btn btn-primary"}
          onClick={onConfirm}
          disabled={busy || !unlocked}
        >
          {confirmLabel ?? t("common.confirm")}
        </button>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// A person's name — or a branch's, a department's, a position's.
//
// The server sends both languages (see @shared/names) and this picks. Two
// things it does that a bare `pick()` at the call site does not:
//
//   1. FALLS BACK TO THE LATIN NAME, never to nothing. `pick` already does
//      this; stating it here is what stops somebody "fixing" an empty cell by
//      transliterating, which would put a machine's guess at how a person
//      spells their own name in front of them.
//
//   2. ISOLATES A LATIN RUN ON AN ARABIC PAGE. "Argelyn Tumacdang" inside an
//      RTL line is a left-to-right run, and the bidi algorithm drags it and
//      the punctuation around it to the wrong end — the same bug already
//      fixed for dates and for the topbar clock. `.ltr-run` is the existing
//      treatment: `unicode-bidi: isolate; direction: ltr`.
//
// The isolation keys off what is ACTUALLY being shown, not off whether an
// Arabic name exists: a row whose `nameAr` was filled in with Latin text —
// which happens — needs it just as much as a null one.
// ─────────────────────────────────────────────────────────────────────────────

const ARABIC_LETTER = /[\u0600-\u06FF]/;

export function Name({ en, ar }: {
  readonly en: string | null | undefined;
  readonly ar?: string | null;
}): ReactElement {
  const { locale, pick } = useI18n();
  const shown = pick(en, ar);
  if (locale === "ar" && shown !== "" && !ARABIC_LETTER.test(shown)) {
    return <span className="ltr-run">{shown}</span>;
  }
  return <>{shown}</>;
}

/**
 * Money, formatted consistently everywhere. `showCurrency` defaults to true;
 * a dense table that states its currency once (a column header, a page title)
 * can pass false to show the bare number instead of repeating "SAR" on every
 * row.
 */
export function Money({ value, showCurrency = true }: { value: string | number; showCurrency?: boolean }): ReactElement {
  const { locale } = useI18n();
  const n = typeof value === "number" ? value : Number.parseFloat(value);
  const safe = Number.isFinite(n) ? n : 0;
  return (
    <span style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
      {formatNumber(safe, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      {showCurrency && <>{" "}{locale === "ar" ? "ر.س" : "SAR"}</>}
    </span>
  );
}
